import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { I18nextProvider, useTranslation } from "react-i18next";
import { DEFAULT_THEME, Spinner, ThemeProvider, ToastProvider, useTheme } from "@aigntx/ui";
import { DEFAULT_AI } from "@aigntx/ai-gateway";
import i18n from "./i18n";
import { endpoints } from "./api";
import type { TenantConfig } from "./types";
import { TenantProvider } from "./tenant";
import { AuthProvider, useAuth } from "./auth";
import { NotifyProvider } from "./notify";
import { Layout } from "./Layout";
import { LoginPage } from "./pages/LoginPage";
import { TenantLandingPage, TenantLoginPage } from "./pages/TenantLandingPage";
import { ForgotPage } from "./pages/ForgotPage";
import { ResetPage } from "./pages/ResetPage";
import { PrivacyPage, TermsPage } from "./pages/legal";
import { DashboardPage } from "./pages/DashboardPage";
import { StartPage } from "./pages/StartPage";
import { SettingsPage } from "./pages/SettingsPage";
import { SplashScreen } from "./SplashScreen";
import { CookieConsent } from "./CookieConsent";
import { UpdatePrompt } from "./UpdatePrompt";
import { CompanyPage } from "./pages/CompanyPage";
import { GuidePage } from "./pages/GuidePage";
import { VaultPage } from "./pages/VaultPage";
import { RecordsPage } from "./pages/RecordsPage";
import { ProjectsPage } from "./pages/ProjectsPage";
import { ProjectDetailPage } from "./pages/ProjectDetailPage";
import { ProjectFormsPage } from "./pages/ProjectFormsPage";
import { SupervisionPage } from "./pages/SupervisionPage";
import { RegistersPage } from "./pages/RegistersPage";
import { SiteLogPage } from "./pages/SiteLogPage";
import { FormDetailPage } from "./pages/FormDetailPage";
import { ControlPlanePage } from "./pages/ControlPlanePage";
import { CanvasPage } from "./pages/CanvasPage";
import { useTenant, useTenantPatch } from "./tenant";

function Splash() {
  return (
    <div className="splash">
      <Spinner />
    </div>
  );
}

/** Offline fallback so the shell renders even if the API is unreachable. */
function fallbackTenant(): TenantConfig {
  return {
    slug: "aigntx",
    name: "AigntX",
    type: "platform",
    theme: DEFAULT_THEME,
    profile: {},
    modules: ["core.auth", "core.users", "core.settings"],
    ai: DEFAULT_AI,
    limits: { seats: 0, storageGb: 0 },
  };
}

/** Keeps i18next's language in sync with the theme locale (drives RTL + fonts). */
function LocaleSync() {
  const { theme } = useTheme();
  const { i18n: inst } = useTranslation();
  useEffect(() => {
    if (inst.language !== theme.locale) void inst.changeLanguage(theme.locale);
  }, [theme.locale, inst]);
  return null;
}

/**
 * Once signed in, adopt the user's OWN tenant (branding + enabled modules) and
 * their saved appearance prefs. Boot-time /tenant/resolve keys off the hostname,
 * so without this a tenant without its own subdomain would inherit the platform
 * tenant's modules and never see its own pages. Personal prefs win over the
 * tenant default, so they're applied last.
 */
function UserSettingsLoader() {
  const { user } = useAuth();
  const { setTheme } = useTheme();
  const patchTenant = useTenantPatch();
  useEffect(() => {
    if (!user) return;
    endpoints
      .myTenant()
      .then((cfg) => {
        patchTenant({ name: cfg.name, type: cfg.type, modules: cfg.modules, theme: cfg.theme });
        setTheme(cfg.theme);
      })
      .catch(() => {})
      .finally(() => {
        endpoints
          .getSettings()
          .then((s) => setTheme({ mode: s.mode, locale: s.locale, font: s.font }))
          .catch(() => {});
      });
  }, [user, setTheme, patchTenant]);
  return null;
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RequireAdmin({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (!user?.isPlatformAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function RequireAnyPermission({ perms, children }: { perms: string[]; children: ReactNode }) {
  const { can } = useAuth();
  if (!perms.some((p) => can(p))) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function RequireModule({ module, children }: { module: string; children: ReactNode }) {
  const tenant = useTenant();
  if (!tenant.modules.includes(module)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* A company's own front door — its people start here, not on the platform login. */}
      <Route path="/t/:slug" element={<TenantLandingPage />} />
      <Route path="/t/:slug/login" element={<TenantLoginPage />} />
      <Route path="/forgot" element={<ForgotPage />} />
      <Route path="/reset" element={<ResetPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        {/* the start page is where everyone lands; the dashboard moved beside it */}
        <Route path="/" element={<StartPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/data" element={<VaultPage />} />
        <Route path="/guide" element={<GuidePage />} />
        <Route
          path="/records"
          element={
            <RequireModule module="crm">
              <RecordsPage />
            </RequireModule>
          }
        />
        <Route
          path="/projects"
          element={
            <RequireModule module="eng.projects">
              <ProjectsPage />
            </RequireModule>
          }
        />
        <Route
          path="/projects/:id"
          element={
            <RequireModule module="eng.projects">
              <ProjectDetailPage />
            </RequireModule>
          }
        />
        <Route
          path="/projects/:id/forms"
          element={
            <RequireModule module="eng.projects">
              <ProjectFormsPage />
            </RequireModule>
          }
        />
        <Route
          path="/projects/:id/registers"
          element={
            <RequireModule module="eng.projects">
              <RegistersPage />
            </RequireModule>
          }
        />
        <Route
          path="/projects/:id/logs"
          element={
            <RequireModule module="eng.projects">
              <SiteLogPage />
            </RequireModule>
          }
        />
        <Route
          path="/projects/:id/supervision"
          element={
            <RequireModule module="eng.projects">
              <SupervisionPage />
            </RequireModule>
          }
        />
        <Route
          path="/projects/:id/forms/:formId"
          element={
            <RequireModule module="eng.projects">
              <FormDetailPage />
            </RequireModule>
          }
        />
        <Route path="/settings" element={<SettingsPage />} />
        <Route
          path="/company"
          element={
            <RequireAnyPermission perms={["users.read", "tenant.manage"]}>
              <CompanyPage />
            </RequireAnyPermission>
          }
        />
        <Route
          path="/admin"
          element={
            <RequireAdmin>
              <ControlPlanePage />
            </RequireAdmin>
          }
        />
        {/* Platform canvas — admin-gated here and again on every API route. */}
        <Route
          path="/canvas"
          element={
            <RequireAdmin>
              <CanvasPage />
            </RequireAdmin>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  const [tenant, setTenant] = useState<TenantConfig | null>(null);
  const [ready, setReady] = useState(false);
  const [minElapsed, setMinElapsed] = useState(false);
  const [splashGone, setSplashGone] = useState(false);

  useEffect(() => {
    endpoints
      .resolveTenant()
      .then(setTenant)
      .catch(() => setTenant(fallbackTenant()))
      .finally(() => setReady(true));
  }, []);

  // Splash shows for a minimum of 3s, then fades once the tenant is loaded.
  useEffect(() => {
    const id = window.setTimeout(() => setMinElapsed(true), 3000);
    return () => window.clearTimeout(id);
  }, []);

  const booting = !ready || !tenant || !minElapsed;
  useEffect(() => {
    if (booting) return;
    const id = window.setTimeout(() => setSplashGone(true), 550);
    return () => window.clearTimeout(id);
  }, [booting]);

  return (
    <>
      {!splashGone && <SplashScreen exiting={!booting} name={tenant?.name} />}
      {ready && tenant && (
        <ThemeProvider initial={tenant.theme}>
          <I18nextProvider i18n={i18n}>
            <ToastProvider>
              <TenantProvider value={tenant}>
                <AuthProvider>
                  <NotifyProvider>
                    <BrowserRouter>
                      <LocaleSync />
                      <UserSettingsLoader />
                      <AppRoutes />
                      <CookieConsent />
                      <UpdatePrompt />
                    </BrowserRouter>
                  </NotifyProvider>
                </AuthProvider>
              </TenantProvider>
            </ToastProvider>
          </I18nextProvider>
        </ThemeProvider>
      )}
    </>
  );
}
